/** @jsxImportSource @emotion/react */
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, Redirect } from "react-router-dom";
import Navbar from "component/Navbar";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import FinalStep from "./FinalStep";
import { CarbonFootprint, setFormData } from "utils/carbonFootprint";
import { useUpdateCarbon, useGetCarbon } from "utils/hooks/carbon.hook";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useGetPlants } from "utils/hooks/plant.hook";
import UnsavedChangeDialog from "component/UnsavedChangeDialog";

function ExistingCarbonFootprintFirstPart() {
  const theme = useTheme();
  const [redirectionPath, setRedirectionPath] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [valuesChanged, setValuesChanged] = useState(false);
  const params = useParams<{ id: string }>();
  const { plants } = useGetPlants();
  const { mutate: update } = useUpdateCarbon(params.id);
  const { isLoading, isError, carbon } = useGetCarbon(params.id);
  const methods = useForm<CarbonFootprint>({
    mode: "onChange",
    defaultValues: {
      reactionNumber: 0,
      reactionNumberWithInformation: 0,
      reactionNumberWithoutInformation: 0,
    },
  });
  const { watch, setValue } = methods;

  const handleStep = (type: "decrement" | "increment") => {
    setStep((step) => (type === "decrement" ? step - 1 : step + 1));
  };

  useEffect(() => {
    if (carbon) setFormData(carbon, setValue);
  }, [carbon, setValue]);

  const handleSave = () => {
    const data = watch();
    update(data);
  };

  if (isLoading) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return <Redirect to="/carbon_footprint/first_part/new" />;
  }

  return (
    <>
      <UnsavedChangeDialog
        redirectionPath={redirectionPath}
        setRedirectionPath={setRedirectionPath}
        valuesChanged={valuesChanged}
        setValuesChanged={setValuesChanged}
        saveAction={handleSave}
        watch={watch}
      />
      <div
        css={{
          flexGrow: 1,
          backgroundColor: theme.white,
          color: theme.black,
          display: "flex",
        }}
      >
        <Navbar customRedirection={setRedirectionPath} />
        <FormProvider {...methods}>
          <form
            css={{
              flexGrow: 1,
              maxHeight: "100vh",
              overflowY: "auto",
            }}
          >
            {step === 1 && (
              <Step1
                handleSaveBeforeLeave={setRedirectionPath}
                handleStep={handleStep}
              />
            )}
            {step === 2 && <Step2 handleStep={handleStep} />}
            {step === 3 && <Step3 handleStep={handleStep} />}
            {step === 4 && (
              <Step4
                setValuesChanged={setValuesChanged}
                plants={plants}
                handleStep={handleStep}
              />
            )}
            {step === 5 && <FinalStep setValuesChanged={setValuesChanged} />}
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export default ExistingCarbonFootprintFirstPart;
